import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { UpsaleStrategy } from '../../types';
import {
	getAvailableInsuranceCodes,
	getOrder as getCheckoutOrder,
	getPriorityInsuranceProgram,
	getSelectedInsuranceCodes
} from '../../../Checkout/store/order/selectors';
import { getCheckinOrder } from '../../../Modules/Checkin/store/order/selectors';
import { useToggleable } from '../../../hooks';
import { useInsuranceUpsaleConfig } from '../../hooks';
import { InsuranceGoal, reachGoal } from '../../../analytics';
import { getFunnelParameters } from '../../../DataLayerAnalytics/utils';
import { Mode } from '../../../types';
import { ModeContext, useConfig } from '../../../context';
import { CheckoutState } from '../../../Checkout/store/reducers';

interface UserInsuranceUpsaleModalProps {
	strategy: UpsaleStrategy;
	availableInsuranceCodes?: string[];
	selectedInsuranceCodes?: string[];
}

export const useInsuranceUpsaleModal = ({
	strategy,
	availableInsuranceCodes,
	selectedInsuranceCodes
}: UserInsuranceUpsaleModalProps) => {
	const mode = useContext(ModeContext);

	const {
		global: {
			insurance: { multiplePriorityInsurances }
		}
	} = useConfig();

	const order = mode === Mode.Checkin ? useSelector(getCheckinOrder) : useSelector(getCheckoutOrder);
	const insuranceAvailableCodes = useSelector(getAvailableInsuranceCodes);
	const insuranceSelectedCodes = useSelector(getSelectedInsuranceCodes);
	const priorityInsurance = useSelector((state: CheckoutState) =>
		getPriorityInsuranceProgram(state, multiplePriorityInsurances)
	);

	const {
		isOpen: isUpsaleOpen,
		setOpen: setUpsaleOpen,
		open: openUpsaleModal,
		close: closeUpsaleModal
	} = useToggleable(false);

	const insuranceUpsaleConfig = useInsuranceUpsaleConfig();

	const availableCodes = useMemo(() => {
		return availableInsuranceCodes ?? insuranceAvailableCodes;
	}, [availableInsuranceCodes, insuranceAvailableCodes]);

	const selectedCodes = useMemo(() => {
		return selectedInsuranceCodes ?? insuranceSelectedCodes;
	}, [selectedInsuranceCodes ?? insuranceSelectedCodes]);

	const isNeedToShowInsuranceUpsale = useMemo(() => {
		if (!priorityInsurance) {
			return false;
		}

		if (!insuranceUpsaleConfig.getIsNeedToShow(strategy)) {
			return false;
		}

		// if the priority insurance is not present in the list, then we skip the step
		if (!availableCodes.includes(priorityInsurance.code)) {
			return false;
		}

		return !selectedCodes.includes(priorityInsurance.code);
	}, [strategy, priorityInsurance, availableCodes, selectedCodes, insuranceUpsaleConfig.getIsNeedToShow]);

	const analyticsFunnelPurchase = useMemo(() => getFunnelParameters(order), [order]);

	const onCloseUpsaleModal = useCallback(() => {
		insuranceUpsaleConfig.onApplyStrategy(strategy);
		closeUpsaleModal();
	}, [strategy, insuranceUpsaleConfig.onApplyStrategy, closeUpsaleModal]);

	const onAgree = useCallback(
		async (callback?: (insuranceCodes?: string[]) => Promise<void>) => {
			reachGoal(InsuranceGoal.InsurancePopupYes, { funnel_purchase: analyticsFunnelPurchase });
			if (priorityInsurance) {
				// if the priority insurance is present in the list, then we skip the request for addition
				if (selectedCodes.includes(priorityInsurance.code)) {
					return;
				}

				onCloseUpsaleModal();

				await callback?.(priorityInsurance.code ? [priorityInsurance.code] : undefined);

				return;
			}

			onCloseUpsaleModal();
		},
		[priorityInsurance, selectedCodes, onCloseUpsaleModal]
	);

	const onDisagree = useCallback(
		async (callback?: () => Promise<void>) => {
			reachGoal(InsuranceGoal.InsurancePopupNo, { funnel_purchase: analyticsFunnelPurchase });
			onCloseUpsaleModal();

			await callback?.();
		},
		[onCloseUpsaleModal]
	);

	const onContinue = useCallback(
		async (callback: () => Promise<void> | void) => {
			if (isNeedToShowInsuranceUpsale) {
				openUpsaleModal();

				return;
			}

			await callback();
		},
		[openUpsaleModal, isNeedToShowInsuranceUpsale]
	);

	return {
		// data
		priorityInsurance,
		isUpsaleOpen,
		isNeedToShowInsuranceUpsale,

		// methods
		setUpsaleOpen,
		onCloseUpsaleModal,
		onAgree,
		onDisagree,
		onContinue
	};
};
